///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Geometry } from "@stovokor/shader/Geometry"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { VertexArray } from "@stovokor/shader/VertexArray"
import { type StovokorContext } from "@stovokor/StovokorContext"

interface Entry {
	vertexArray: VertexArray
	refs: number
}

/**
 * One vertex array per (program link, geometry), shared by every renderer
 * drawing that pair. A VAO captures attribute pointers, which depend only
 * on the program's attribute locations and the geometry's buffers, so
 * three thousand marker renderers on one shared sphere mesh need one
 * array, not three thousand: the state cache then skips the
 * bindVertexArray between consecutive draws instead of missing on every
 * one. Per-geometry renderers (tiles) still get an array each, as before.
 *
 * Entries are keyed by the program's id and link version, so after a
 * relink renderers acquire a fresh array with the new locations while the
 * old one lingers until its last user releases it.
 */
export class VertexArrayCache {
	private context: StovokorContext
	private entries = new WeakMap<Geometry, Map<string, Entry>>()

	constructor(context: StovokorContext) {
		this.context = context
	}

	/** Shared array for this program link and geometry; pair with release() */
	public acquire(shader: ShaderProgram, geometry: Geometry): VertexArray {
		let byProgram = this.entries.get(geometry)
		if(byProgram === undefined) {
			byProgram = new Map()
			this.entries.set(geometry, byProgram)
		}
		const key = VertexArrayCache.key(shader)
		let entry = byProgram.get(key)
		if(entry !== undefined && !entry.vertexArray.isCurrent(geometry.indexBuffer)) {
			// The geometry replaced its buffers under the cached array
			// (PolylineGeometry layout change); holders of the old array are
			// renderers their nodes have already dropped, so start over
			entry.vertexArray.destroy()
			byProgram.delete(key)
			entry = undefined
		}
		if(entry === undefined) {
			entry = {
				vertexArray: new VertexArray(this.context, shader.attributeLocationsFrom(geometry), geometry.indexBuffer),
				refs: 0
			}
			byProgram.set(key, entry)
		}
		entry.refs += 1
		return entry.vertexArray
	}

	/**
	 * Drop a reference taken with acquire(); the array is destroyed with
	 * its last one. Looked up by the array rather than the program's
	 * current key: after a relink the holder's array belongs to the old
	 * version's entry, which other holders may still be drawing with
	 */
	public release(geometry: Geometry, vertexArray: VertexArray): void {
		const byProgram = this.entries.get(geometry)
		if(byProgram !== undefined) {
			for(const [key, entry] of byProgram) {
				if(entry.vertexArray === vertexArray) {
					entry.refs -= 1
					if(entry.refs <= 0) {
						entry.vertexArray.destroy()
						byProgram.delete(key)
					}
					return
				}
			}
		}
		// Not tracked (geometry collected or never cached): just free it
		vertexArray.destroy()
	}

	private static key(shader: ShaderProgram): string {
		return `${shader.id}:${shader.version}`
	}
}
